import React, { useState, useEffect } from 'react';
import databaseNotificationService from '../services/databaseNotificationService';

const AssignmentNotificationPopup = ({ employeeId }) => {
  const [notifications, setNotifications] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!employeeId) {
      console.log('⚠️ AssignmentNotificationPopup: No employeeId provided');
      return;
    }

    console.log('🔔 AssignmentNotificationPopup initialized for employee:', employeeId);

    // Check for unread notifications from database
    const checkNotifications = async () => {
      try {
        console.log('🔍 Fetching notifications from database for employee:', employeeId);
        
        const response = await databaseNotificationService.getUnreadNotifications();
        const unreadNotifications = response.notifications || [];
        
        console.log('📬 Unread notifications found:', unreadNotifications.length);
        
        if (unreadNotifications.length > 0) {
          console.log('✅ Showing popup with notifications:', unreadNotifications);
          setNotifications(unreadNotifications);
          setShowPopup(true);
        } else {
          console.log('ℹ️ No unread notifications');
          setShowPopup(false);
        }
      } catch (error) {
        console.error('❌ Error fetching notifications:', error);
      }
    };

    // Check immediately
    checkNotifications();

    // Check every 3 seconds for real-time updates
    const interval = setInterval(checkNotifications, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [employeeId]);

  const handleDismiss = async (notificationId) => {
    try {
      await databaseNotificationService.markAsRead(notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      
      if (notifications.length <= 1) {
        setShowPopup(false);
      }
    } catch (error) {
      console.error('❌ Error dismissing notification:', error);
    }
  };

  const handleDismissAll = async () => {
    try {
      await databaseNotificationService.markAllAsRead();
      setNotifications([]);
      setShowPopup(false);
    } catch (error) {
      console.error('❌ Error dismissing all notifications:', error);
    }
  };

  if (!showPopup || notifications.length === 0) return null;

  return (
    <>
      {/* Add custom CSS for animations */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }

        .notification-enter {
          animation: slideInRight 0.5s ease-out, bounce 0.6s ease-in-out 0.5s;
        }

        .notification-glow {
          box-shadow: 0 0 20px rgba(20, 184, 166, 0.4);
        }
      `}</style>

      <div className="fixed top-4 right-4 z-50 max-w-md space-y-3">
        {notifications.map((notification, index) => (
          <div
            key={notification._id}
            className="notification-enter notification-glow bg-white rounded-xl shadow-2xl border-l-4 border-teal-500 p-5 transform transition-all hover:scale-105"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Header with Icon and Close */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base">{notification.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(notification.createdAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDismiss(notification._id)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center transition-all text-xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Lead Details Card */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4 mb-3 border border-teal-200">
              <div className="flex items-start gap-2 mb-2">
                <span className="text-lg">👤</span>
                <div className="flex-1">
                  <p className="text-sm font-bold text-gray-900">
                    {notification.data?.leadName || notification.leadId?.customerName || 'Customer'}
                  </p>
                </div>
              </div>
              
              {notification.data?.leadEmail && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm">📧</span>
                  <p className="text-xs text-gray-700 font-medium">
                    {notification.data.leadEmail}
                  </p>
                </div>
              )}
              
              {notification.data?.serviceType && (
                <div className="flex items-center gap-2">
                  <span className="text-sm">🎫</span>
                  <span className="text-xs font-semibold text-teal-700 bg-white px-2 py-1 rounded-full">
                    {notification.data.serviceType.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
              )}
              
              <div className="mt-2">
                <p className="text-xs text-gray-600">{notification.message}</p>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={() => handleDismiss(notification._id)}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-sm font-semibold rounded-lg hover:from-teal-700 hover:to-teal-800 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              ✓ Got it!
            </button>
          </div>
        ))}

        {/* Dismiss All Button */}
        {notifications.length > 1 && (
          <button
            onClick={handleDismissAll}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white text-sm font-semibold rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all shadow-md hover:shadow-lg"
          >
            🗑️ Dismiss All ({notifications.length})
          </button>
        )}
      </div>
    </>
  );
};

export default AssignmentNotificationPopup;
